import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  TextField,
  Box,
  Typography,
  Container,
  Alert,
  Paper,
  InputAdornment,
  IconButton,
  Fade,
  CircularProgress,
  Divider,
  Link,
} from '@mui/material';
import {
  Email,
  Lock,
  Visibility,
  VisibilityOff,
  LoginOutlined,
} from '@mui/icons-material';
import { styled, alpha } from '@mui/material/styles';

const LoginPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: 12,
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
  backgroundImage: `linear-gradient(135deg, ${alpha(theme.palette.primary.light, 0.06)} 0%, ${alpha(
    theme.palette.primary.main,
    0.02
  )} 100%)`,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  maxWidth: 450,
  width: '100%',
  margin: '0 auto',
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  width: 120,
  height: 120,
  borderRadius: '50%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  margin: '0 auto',
  marginBottom: theme.spacing(3),
  padding: theme.spacing(1),
  backgroundColor: theme.palette.background.paper,
  boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.15)}`,
  border: `3px solid ${alpha(theme.palette.primary.main, 0.1)}`,
}));

const StyledButton = styled(Button)(({ theme }) => ({
  padding: theme.spacing(1.2),
  borderRadius: 8,
  fontSize: '1rem',
  fontWeight: 600,
  textTransform: 'none',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 16px rgba(0, 0, 0, 0.15)',
  },
}));

const AdminLogin = ({ setIsAuthenticated }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const apiUrl = process.env.REACT_APP_API_URL;

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Basic validation
    if (!email.trim()) {
      setError('Inserisci la tua email');
      setLoading(false);
      return;
    }

    if (!password.trim()) {
      setError('Inserisci la tua password');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post(`${apiUrl}/admin/login`, {
        email,
        password,
      });
      
      // Success - save token and redirect
      localStorage.setItem('adminToken', response.data.token);
      setIsAuthenticated(true);
      
      // Small delay for better UX
      setTimeout(() => {
        navigate('/bookings');
      }, 500);
      
    } catch (error) {
      console.error('Login error:', error);
      if (error.response && error.response.status === 401) {
        setError('Email o password non validi');
      } else {
        setError('Si è verificato un errore durante il login. Riprova più tardi.');
      }
      setLoading(false);
    }
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Container 
      component="main" 
      maxWidth="sm" 
      sx={{ 
        height: '100vh', 
        display: 'flex', 
        flexDirection: 'column', 
        justifyContent: 'center',
        py: 4
      }}
    >
      <Fade in={true} timeout={800}>
        <LoginPaper elevation={6}>
          <LogoContainer>
            <img
              src="/logo192.png"
              alt="Logo Area Sosta Camper"
              style={{ maxWidth: '100%', maxHeight: '100%', borderRadius: '50%' }}
            />
          </LogoContainer>

          <Typography variant="h4" align="center" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
            Area Sosta Camper
          </Typography>
          
          <Typography variant="h6" align="center" gutterBottom color="text.secondary" sx={{ mb: 3 }}>
            Corinaldo
          </Typography>

          <Divider sx={{ my: 3 }}>
            <Typography variant="body2" color="text.secondary">
              Accesso Amministratore
            </Typography>
          </Divider>

          {error && (
            <Alert 
              severity="error" 
              sx={{ mb: 3, borderRadius: 2 }}
              onClose={() => setError('')}
            >
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleLogin} sx={{ mt: 1 }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email"
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email color="primary" />
                  </InputAdornment>
                ),
              }}
              sx={{ 
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }}
            />
            
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type={showPassword ? 'text' : 'password'}
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock color="primary" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleTogglePasswordVisibility}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ 
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }}
            />

            <StyledButton
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              size="large"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <LoginOutlined />}
            >
              {loading ? 'Accesso in corso...' : 'Accedi'}
            </StyledButton>
            
            <Box sx={{ mt: 4, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                © {new Date().getFullYear()} Area Sosta Camper Corinaldo
              </Typography>
              <Link href="#" variant="body2" color="primary" sx={{ mt: 0.5, display: 'inline-block' }}>
                Hai bisogno di aiuto?
              </Link>
            </Box>
          </Box>
        </LoginPaper>
      </Fade>
    </Container>
  );
};

export default AdminLogin;